// MediaUploader - Upload de imagens/vídeos inline
import React, { useRef } from 'react';
import { Image as ImageIcon, Video } from 'lucide-react';
import { useUploadMedia } from '@/hooks/useMedia';
import toast from 'react-hot-toast';
import ImageEditorModal from '@/components/Admin/ImageEditorModal';
import { useState } from 'react';

const MediaUploader = ({ editor, type = 'image' }) => {
  const fileInputRef = useRef(null);
  const uploadMutation = useUploadMedia();

  // Estados para o Cropper
  const [cropperData, setCropperData] = useState({
    isOpen: false,
    image: null,
    fileName: '',
    originalFile: null
  });

  const handleFileSelect = async (e, skipCrop = false) => {
    const file = e.target.files?.[0] || e.target.files[0];
    if (!file) return;

    // Se for imagem e não tivermos pulado o crop, abrir o cropper
    if (type === 'image' && !skipCrop) {
      const reader = new FileReader();
      reader.onload = () => {
        setCropperData({
          isOpen: true,
          image: reader.result,
          fileName: file.name,
          originalFile: file
        });
      };
      reader.readAsDataURL(file);
      return;
    }

    try {
      toast.loading('Fazendo upload...');
      const response = await uploadMutation.mutateAsync({ file });
      const media = response?.data || response;

      const mediaUrl = media?.url || media?.Url;
      if (!mediaUrl) {
        throw new Error('URL não retornada pelo servidor');
      }

      if (type === 'image') {
        editor.chain().focus().setImage({ src: mediaUrl }).run();
      } else if (type === 'video') {
        // Para vídeos, inserir como iframe ou link
        editor.chain().focus().insertContent(`<iframe src="${mediaUrl}" width="560" height="315" frameborder="0" allowfullscreen></iframe>`).run();
      }

      toast.dismiss();
      toast.success('Mídia adicionada com sucesso!');
    } catch (error) {
      toast.dismiss();
      toast.error('Erro ao fazer upload da mídia');
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleCropComplete = async (croppedBlob) => {
    const file = new File([croppedBlob], cropperData.fileName, { type: 'image/jpeg' });
    setCropperData(prev => ({ ...prev, isOpen: false }));
    // Simular evento para handleFileSelect
    await handleFileSelect({ target: { files: [file] } }, true);
  };

  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      if (items[i].kind === 'file') {
        const file = items[i].getAsFile();
        if (file) {
          e.preventDefault();
          // Simular um evento de seleção de arquivo para aproveitar a lógica existente
          const mockEvent = { target: { files: [file] } };
          await handleFileSelect(mockEvent);
          break;
        }
      }
    }
  };

  return (
    <>
      <button
        onClick={() => fileInputRef.current?.click()}
        onPaste={handlePaste}
        title={type === 'image' ? 'Inserir imagem' : 'Inserir vídeo'}
        tabIndex="0"
        type="button"
      >
        {type === 'image' ? <ImageIcon size={18} /> : <Video size={18} />}
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept={type === 'image' ? 'image/*' : 'video/*'}
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      {/* Modal de Corte de Imagem */}
      <ImageEditorModal
        isOpen={cropperData.isOpen}
        onClose={() => setCropperData(prev => ({ ...prev, isOpen: false }))}
        image={cropperData.image}
        onCropComplete={handleCropComplete}
        aspect={16 / 9}
      />
    </>
  );
};

export default MediaUploader;
